import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { updatePaymentStatus } from '../../services/contractService';
import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/outline';
import useAuthStore from '../../store/authStore';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { contractId, paymentId } = useParams<{ contractId: string; paymentId: string }>();
  const { userData } = useAuthStore();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Redirect if not a client
  if (userData?.role !== 'client') {
    return <Navigate to="/unauthorized" replace />;
  }

  const paymentMethods = [
    { id: 'credit_card', name: 'بطاقة ائتمان' },
    { id: 'mada', name: 'مدى' },
    { id: 'apple_pay', name: 'آبل باي' },
    { id: 'bank_transfer', name: 'تحويل بنكي' },
  ];

  const handlePayment = async () => {
    if (!selectedMethod) {
      alert('الرجاء اختيار طريقة دفع');
      return;
    }

    if (!contractId || !paymentId) {
      alert('خطأ في معرّفات الدفع');
      return;
    }

    setIsProcessing(true);
    try {
      // محاكاة عملية الدفع
      await new Promise((resolve) => setTimeout(resolve, 2000));
      // تحديث حالة الدفع في قاعدة البيانات
      await updatePaymentStatus(contractId, paymentId, 'مدفوع');
      setPaymentSuccess(true);
    } catch (error) {
      console.error('فشلت عملية الدفع:', error);
      alert('فشلت عملية الدفع. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!contractId || !paymentId) {
    return (
      <div className="p-5 text-center">
        <p>خطأ في معرّفات الدفع</p>
      </div>
    );
  }

  return (
    <div className="p-5 bg-gray-50 min-h-screen text-right dir-rtl font-tajawal mb-24">
      <div className="flex items-center mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200">
          <ArrowRightIcon className="h-5 w-5 text-gray-700" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mr-4">إتمام الدفع</h2>
      </div>

      <div className="bg-white p-6 rounded-3xl shadow-md mb-6">
        <h2 className="font-bold text-lg mb-4">تفاصيل الدفع</h2>
        <div className="space-y-3 text-gray-700">
          <div className="flex justify-between">
            <span>معرف العقد:</span>
            <span className="font-medium">{contractId}</span>
          </div>
          <div className="flex justify-between">
            <span>معرف الدفعة:</span>
            <span className="font-medium">{paymentId}</span>
          </div>
        </div>
      </div>

      {paymentSuccess ? (
        <div className="bg-white p-6 rounded-3xl shadow-md text-center mb-6">
          <div className="flex flex-col items-center">
            <div className="bg-green-100 p-3 rounded-full mb-4">
              <CheckCircleIcon className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">تمت عملية الدفع بنجاح</h3>
            <p className="text-gray-600 mb-6">شكراً لك على إتمام عملية الدفع بنجاح</p>
            
            <div className="w-full max-w-xs space-y-3">
              <button
                onClick={() => console.log('Issue receipt')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'oklch(0.55 0.24 262.91)',
                  color: '#ffffff',
                  borderRadius: '0.75rem',
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  outline: 'none',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
                onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
                onBlur={e => e.currentTarget.style.boxShadow = 'none'}
              >
                إصدار سند قبض
              </button>
              
              <button
                onClick={() => navigate(`/contracts/${contractId}`)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'transparent',
                  color: 'oklch(0.55 0.24 262.91)',
                  borderRadius: '0.75rem',
                  fontWeight: 500,
                  border: '1px solid oklch(0.55 0.24 262.91)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  outline: 'none',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                  e.currentTarget.style.color = 'oklch(0.5 0.24 262.91)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'oklch(0.55 0.24 262.91)';
                }}
                onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.5)'}
                onBlur={e => e.currentTarget.style.boxShadow = 'none'}
              >
                إنهاء العملية
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-3xl shadow-md">
          <h3 className="font-bold text-lg mb-4">اختر طريقة الدفع</h3>
          <ul className="space-y-4 mb-6">
            {paymentMethods.map((method) => (
              <li key={method.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <input
                  type="radio"
                  id={method.id}
                  name="paymentMethod"
                  value={method.id}
                  checked={selectedMethod === method.id}
                  onChange={() => setSelectedMethod(method.id)}
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <label htmlFor={method.id} className="mr-3 text-gray-700 flex-1 cursor-pointer">
                  {method.name}
                </label>
              </li>
            ))}
          </ul>
          <button
            onClick={handlePayment}
            disabled={isProcessing}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              color: '#ffffff',
              fontWeight: 500,
              border: 'none',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              backgroundColor: isProcessing ? '#9ca3af' : 'oklch(0.55 0.24 262.91)',
              transition: 'background-color 0.2s',
              fontSize: '1rem',
              fontFamily: 'inherit',
              outline: 'none',
              opacity: isProcessing ? 0.8 : 1
            }}
            onMouseOver={e => !isProcessing && (e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)')}
            onMouseOut={e => !isProcessing && (e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)')}
            onFocus={e => !isProcessing && (e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)')}
            onBlur={e => e.currentTarget.style.boxShadow = 'none'}
          >
            {isProcessing ? 'جاري المعالجة...' : 'تأكيد الدفع'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
